import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureError } from "@/lib/sentry";

const mono = { fontFamily: '"JetBrains Mono", monospace' } as const;
const serif = { fontFamily: '"Cormorant Garamond", serif' } as const;

function Fallback({ resetError }: { resetError: () => void }) {
  return (
    <div
      className="min-h-screen bg-paper text-ink flex items-center justify-center"
      style={{ padding: "24px" }}
    >
      <div style={{ maxWidth: "560px", textAlign: "center" }}>
        <p
          className="font-bold uppercase tracking-[0.05em] text-burgundy"
          style={{ ...mono, fontSize: "11px" }}
        >
          Something broke
        </p>
        <h1
          className="text-[32px] md:text-[44px] font-bold leading-[1.15] text-ink"
          style={{ ...serif, marginTop: "24px" }}
        >
          Something went wrong. We're looking into it.
        </h1>
        <div
          style={{
            marginTop: "24px",
            marginLeft: "auto",
            marginRight: "auto",
            width: "60px",
            height: "2px",
            backgroundColor: "#8B1E1E",
          }}
        />
        <div
          className="flex flex-wrap justify-center gap-3"
          style={{ marginTop: "32px" }}
        >
          <button
            type="button"
            onClick={() => {
              resetError();
              if (typeof window !== "undefined") window.location.reload();
            }}
            className="interactive-lift font-bold uppercase tracking-[0.05em] bg-burgundy text-paper"
            style={{ ...mono, fontSize: "11px", padding: "14px 24px" }}
          >
            Refresh the page
          </button>
          <a
            href="/"
            className="interactive-lift font-bold uppercase tracking-[0.05em]"
            style={{
              ...mono,
              fontSize: "11px",
              padding: "14px 24px",
              color: "#5A534A",
              border: "1px solid #5A534A",
            }}
          >
            Back home
          </a>
        </div>
      </div>
    </div>
  );
}

interface State {
  hasError: boolean;
}

export class SentryBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureError(error, { componentStack: info.componentStack });
  }

  reset = () => this.setState({ hasError: false });

  render() {
    if (this.state.hasError) return <Fallback resetError={this.reset} />;
    return this.props.children;
  }
}
